import { baseApiUrl } from 'src/config';
import { getAccessToken, getRealmName } from 'src/routes/AuthContext';

/**
 * BRK-1851: GET /api/document/mds-dashboard/patients (contract v1.1 §5.1).
 * Server computes all due-intelligence fields on the facility's wall-clock
 * day; the UI only renders them. Patients are enumerated without EOC joins —
 * quarterly-screen-only patients always appear.
 */

export type MdsSortOption = 'name_asc' | 'name_desc' | 'deadline_asc';

export type MdsPatientListParams = {
  facilityUuid: string;
  active: boolean;
  inactive: boolean;
  search?: string;
  needsAttentionOnly: boolean;
  sort: MdsSortOption;
  page: number;
  pageSize: number;
};

export type MdsNextAssessment = {
  assessmentUuid: string;
  assessmentType: string | null;
  ard: string | null;
};

export type MdsDueStatus = {
  fiveDayWindowDay: number | null;
  ardInDays: number | null;
  completeByDate: string | null;
  completeInDays: number | null;
  quarterlyDueDate: string | null;
  missingGgCount: number | null;
  newAdmission: boolean | null;
};

export type MdsWorksheetStatus = {
  state: 'NOT_STARTED' | 'SUBMITTED';
  version: number | null;
  lastActorRole: string | null;
  // BRK-1898: resolved display name; null when the iamId is unresolvable
  lastActorName: string | null;
  lastAt: string | null;
};

export type MdsPatientRow = {
  patientUuid: string;
  patientName: string;
  mrn: string | null;
  active: boolean;
  therapyStatus: string | null;
  admittedDate: string | null;
  nextAssessment: MdsNextAssessment | null;
  dueStatus: MdsDueStatus;
  ggCompleteness: { scored: number; total: number } | null;
  worksheetStatus: MdsWorksheetStatus;
};

export type MdsPatientListResponse = {
  content: MdsPatientRow[];
  totalElements: number;
  totalPages: number;
  page: number;
  pageSize: number;
};

export const getMdsPatients = async (
  params: MdsPatientListParams
): Promise<MdsPatientListResponse> => {
  const token = await getAccessToken();
  const query = new URLSearchParams({
    facilityUuid: params.facilityUuid,
    active: String(params.active),
    inactive: String(params.inactive),
    needsAttentionOnly: String(params.needsAttentionOnly),
    sort: params.sort,
    page: String(params.page),
    pageSize: String(params.pageSize),
  });
  if (params.search?.trim()) {
    query.set('search', params.search.trim());
  }

  const response = await fetch(
    `${baseApiUrl}/document/mds-dashboard/patients?${query.toString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'x-tenant-id': getRealmName() || '',
      },
    }
  );
  let body: any = null;
  try {
    body = await response.json();
  } catch (e) {
    // gateway 502/504s return non-JSON bodies — surface a clean message
    throw new Error(`Failed to load MDS patients (HTTP ${response.status})`);
  }
  if (body?.code !== 'OK') {
    // flag-off 404s and validation errors both land here; caller shows an
    // error/empty state rather than crashing
    throw new Error(body?.message || 'Failed to load MDS patients');
  }
  return body.data as MdsPatientListResponse;
};
